import { create } from "zustand";
import { TOTAL_STEPS, steps } from "@/data/steps";

const useQuizStore = create((set, get) => ({
  currentStep: 1,
  quizData: {},
  preferredUnit: "imperial",
  isValid: false,

  loadingSteps: [
    { text: "Evaluating your answers...", delay: 0 },
    { text: "Analyzing your results...", delay: 1800 },
    { text: "Determining your gut type...", delay: 3600 },
    { text: "Building your summary...", delay: 5400 },
  ],

  setCurrentStep: (step) => {
    set({ currentStep: step });
    setTimeout(() => get().updateValidity());
  },

  goToNext: () => {
    const { currentStep } = get();
    if (currentStep < TOTAL_STEPS) {
      set({ currentStep: currentStep + 1 });
      setTimeout(() => get().updateValidity());
    } else {
      window.location.href = window.cta_link || "#"; // next step after quiz completion
    }
  },

  goToPrevious: () => {
    const { currentStep } = get();
    if (currentStep > 1) {
      set({ currentStep: currentStep - 1 });
      setTimeout(() => get().updateValidity());
    }
  },

  saveData: (field, value) => {
    set((state) => ({
      quizData: {
        ...state.quizData,
        [field]: value,
      },
    }));
    setTimeout(() => get().updateValidity());
  },

  setPreferredUnit: (unit) => {
    set({ preferredUnit: unit });
    setTimeout(() => get().updateValidity());
  },

  updateValidity: () => {
    const { currentStep, quizData, preferredUnit } = get();
    const currentStepData = get().getCurrentStepData();

    let isValid = false;

    if (currentStepData) {
      switch (currentStepData.type) {
        case "multiselect":
          const selected = quizData[currentStepData.field] || [];
          isValid = selected.length > 0;
          break;
        case "info":
        case "summary":
          isValid = true;
          break;
        case "form":
          isValid = false;
          break;
        case "singleSelect":
        case "loading":
          isValid = false;
          break;
        default:
          isValid = false;
      }
    }

    set({ isValid });
  },

  updateFormField: (field, value) => {
    set((state) => ({
      quizData: {
        ...state.quizData,
        [field]: value,
      },
    }));
    setTimeout(() => get().updateValidity());
  },

  toggleSelection: (field, value, isExclusive = false) => {
    const { quizData } = get();
    const current = quizData[field] || [];

    let newSelection;
    if (isExclusive) {
      newSelection = current.includes(value) ? [] : [value];
    } else {
      const withoutExclusive = current.filter((v) => {
        const step = steps.find((s) => s.field === field);
        const option = step?.options?.find((o) => o.value === v);
        return !option?.exclusive;
      });

      if (current.includes(value)) {
        newSelection = withoutExclusive.filter((v) => v !== value);
      } else {
        newSelection = [...withoutExclusive, value];
      }
    }

    set((state) => ({
      quizData: {
        ...state.quizData,
        [field]: newSelection,
      },
    }));
    setTimeout(() => get().updateValidity());
  },

  setSingleSelection: (field, value) => {
    set((state) => ({
      quizData: {
        ...state.quizData,
        [field]: [value],
      },
    }));
    setTimeout(() => get().updateValidity());
  },

  getCurrentStepData: () => {
    const { currentStep } = get();
    return steps[currentStep - 1];
  },

  resetQuiz: () => {
    set({
      currentStep: 1,
      quizData: {},
      preferredUnit: "imperial",
      isValid: false,
    });
  },

  setFormValidity: (isValid) => {
    set({ isValid });
  },
}));

export default useQuizStore;
